import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { DetailsSection, EmptyState } from '@eis/patterns';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { summarizeGroupCore } from '../model/group-core';

/**
 * What the group layer adds to the child it pins: factors, aggregation, billing defaults.
 *
 * Read-only, and that is a statement about the model rather than a limitation of the screen. The
 * group factor lives in `group_core` and never in the child config (the motor client's constraint, service
 * summary §3.2) — which is what lets the same product serve the standalone and the group channel
 * at once. A published asset is immutable, so what a configurator does on this surface is choose
 * WHICH one a version pins; changing what a factor says is a new asset.
 *
 * The panel shows the asset the SELECTED version pins, not "the latest": an in-force Group Policy
 * sits on the version it was activated under, and a panel that showed the newest values would
 * answer a question nobody asked.
 */
export function GroupCorePanel({
  asset,
  pinnedErn,
}: {
  readonly asset: ConfigApi.CoreConfig | undefined;
  readonly pinnedErn: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const summary = summarizeGroupCore(asset);

  if (asset === undefined) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <EmptyState
          compact
          description={
            pinnedErn === ''
              ? t('groupTarifs.groupCore.none')
              : t('groupTarifs.groupCore.unresolved', { ern: pinnedErn })
          }
        />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <DetailsSection
        title={t('groupTarifs.groupCore.title')}
        fields={[
          {
            label: t('groupTarifs.groupCore.aggregation'),
            value: t(`groupTarifs.groupCore.aggregationMode.${summary.aggregationMode}` as never, {
              defaultValue: summary.aggregationMode,
            }),
          },
          {
            label: t('groupTarifs.groupCore.proRata'),
            value: t(`groupTarifs.groupCore.proRataMode.${summary.proRata}` as never, {
              defaultValue: summary.proRata,
            }),
          },
          {
            label: t('groupTarifs.groupCore.billingFrequency'),
            value: summary.billingFrequency,
          },
          { label: t('groupTarifs.groupCore.dunning'), value: summary.dunning },
        ]}
        advancedFields={[
          { label: t('groupTarifs.groupCore.ern'), value: asset.ern },
          { label: t('groupTarifs.groupCore.billingMode'), value: summary.billingMode },
          {
            label: t('groupTarifs.groupCore.conditionBundles'),
            value: summary.conditionBundles,
          },
        ]}
      />

      <Paper variant="outlined" component="section" sx={{ p: 2.5 }}>
        <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
          {t('groupTarifs.groupCore.factors')}
        </Typography>
        {summary.factors.length === 0 ? (
          <EmptyState compact description={t('groupTarifs.groupCore.noFactors')} />
        ) : (
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {summary.factors.map((factor) => (
              <Stack
                key={factor.key}
                component="li"
                direction="row"
                spacing={1.5}
                sx={{ alignItems: 'center' }}
              >
                <Box component="code" sx={{ fontFamily: 'monospace' }}>
                  {factor.key}
                </Box>
                {/* The number a child premium is multiplied by, at CHILD calculation time — which
                    is why a group publish rates through the group context (§3.4). */}
                <Chip
                  size="small"
                  label={t('groupTarifs.groupCore.factorValue', {
                    value: factor.value,
                    type: factor.type,
                  })}
                />
                <Typography variant="caption" color="text.secondary">
                  {factor.appliedAt}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
